// Third Party
import * as React from 'react';
import Image from 'next/image';
import { useWindowSize } from '@hooks';
import { toInteger } from 'lodash';
import moment from 'moment';

// Utils
import { imagekitLoader } from '@utils/imagekit-loader';
import {
  GRAY_PIXEL_DATA_URL,
  enumToStringPropertyTypesAbr,
} from '@utils/constants';
import { formatWholeDollarAmount } from '@utils/money';
import { concatenateAndReplace } from '@utils/test-helpers';
import { GalleryAsset, Listing, PriceDisplay, PropertyType } from '@gql';
import { formatDateMMMD } from '@utils/date-helpers';
import { abbreviateState } from '@utils/abbreviate-state';

// UI
import { Link } from '@components/link';
import { Icon, IconNames } from '@components/icon';
import { FavoriteButton } from '@components/favorite-button';
import { Carousel } from '@components/carousel';

export type ListingPartial = Pick<
  Listing,
  | 'lat'
  | 'lng'
  | 'propertyType'
  | 'city'
  | 'stateOrProvinceLongName'
  | 'slug'
  | 'name'
  | 'guestCapacity'
  | 'numberOfRooms'
  | 'numberOfBathrooms'
  | 'minPrice'
  | 'maxPrice'
  | 'propertyType'
  | 'id'
  | 'isFavorited'
  | 'listingLevel'
  | 'reviewsAverageRating'
  | 'reviewsCount'
> & {
  account: { companyName?: string };
  galleryAssets: Array<Pick<GalleryAsset, 'id' | 'url' | 'description'>>;
  priceDisplay?: Pick<
    PriceDisplay,
    | 'baseAmountCents'
    | 'checkInDate'
    | 'checkOutDate'
    | 'nights'
    | 'subtotalWithoutTaxesCents'
  >;
};

type SliderImage = {
  url: string;
};

interface Props {
  listing: ListingPartial;
  isFavorited: boolean;
  closeMapCard: VoidFunction;
  listingRoute: string;
  onFavoritedSuccess?: (isFavorited: boolean) => void;
  displayStay?: boolean;
}

const smallScreenSize = 640;

const rateBeforeSavings = (nightlyRate: number): number => {
  return nightlyRate * 1.15;
};

export const ListingMapCard: React.FC<Props> = ({
  listing,
  isFavorited,
  closeMapCard,
  onFavoritedSuccess,
  listingRoute,
  displayStay = false,
}) => {
  const { width: screenWidth } = useWindowSize();

  const name = listing.name || '';
  const href = listingRoute;

  const shouldShowAllImages = listing.galleryAssets.length > 1;
  const shouldShowGuestCapacity =
    (!displayStay || !listing.priceDisplay) &&
    (listing.numberOfRooms || listing.guestCapacity) &&
    listing.propertyType !== PropertyType.BoutiqueHotel &&
    listing.propertyType !== PropertyType.BedAndBreakfast;

  let images: SliderImage[] = [];

  if (
    !Array.isArray(listing.galleryAssets) ||
    listing.galleryAssets.length > 0
  ) {
    listing.galleryAssets.forEach((galleryAsset) => {
      if (galleryAsset.url) {
        images.push({ url: galleryAsset.url });
      }
    });
  }

  const imageWidthPx = 84 * 4;
  const imageHeightPx = 48 * 4;

  const location = `${listing.city}, ${abbreviateState(listing.stateOrProvinceLongName)}`;
  const propertyType = listing.propertyType
    ? enumToStringPropertyTypesAbr(listing.propertyType)
    : null;

  return (
    <div
      className="h-full w-full rounded-2xl overflow-hidden bg-white"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className="flex flex-row h-full lg:flex-col bg-white">
        <div className="relative block h-full w-1/3 lg:w-full lg:aspect-[5/3]">
          <Carousel
            showDots={shouldShowAllImages}
            showArrows={shouldShowAllImages && screenWidth > smallScreenSize}
            className="w-full h-48 cursor-pointer"
          >
            {images.map((image, index) => (
              <Link
                to={href || ''}
                key={index}
                target={screenWidth <= smallScreenSize ? undefined : '_blank'}
                className="w-full h-48 cursor-pointer"
              >
                <Image
                  height={imageHeightPx}
                  width={imageWidthPx}
                  src={image.url}
                  alt={'map card img'}
                  placeholder="blur"
                  className="w-auto h-full lg:w-full lg:h-48 object-center object-cover"
                  loader={imagekitLoader}
                  blurDataURL={GRAY_PIXEL_DATA_URL}
                  sizes="400px"
                  lazyBoundary="400px"
                  data-testid={concatenateAndReplace(
                    `${listing.name}-image-${index}`
                  )}
                />
              </Link>
            ))}
          </Carousel>
          <div className="absolute top-0 left-0 pl-2 pt-2">
            <div>
              <button
                style={{ outline: 'none' }}
                type="button"
                className="inline-flex justify-center text-white opacity-100 hover:opacity-70 focus:outline-none font-semibold bg-black/50 p-1.5 rounded-full"
                onClick={closeMapCard}
              >
                <div className="flex items-center justify-center w-3 h-3 lg:hidden">
                  <Icon name={IconNames.X} size="xs" solid={true} />
                </div>
                <div className="hidden lg:flex items-center justify-center w-4 h-4">
                  <Icon name={IconNames.X} solid={true} />
                </div>
              </button>
            </div>
          </div>
        </div>

        <div className="h-full w-2/3 pl-3 lg:w-full lg:h-full">
          <div className="flex flex-col h-full justify-between">
            <div className="flex justify-between pt-2.5 w-full">
              <a
                href={href || ''}
                className="w-full text-gray-dark text-xs font-medium space-y-0.5 items-center justify-center"
              >
                <div className="flex flex-row w-full justify-between space-x-2 max-w-[calc(100%-10px)] items-center pb-0.5 ">
                  <div className="flex-1 text-primary-main font-semibold text-xs line-clamp-1 min-w-0">
                    {name}
                  </div>
                  {/* Ratings */}
                  {listing.reviewsAverageRating ? (
                    <div className="flex-shrink-0 text-xs font-medium whitespace-nowrap text-primary-main">
                      <div className="flex flex-row items-center space-x-0.5">
                        <Icon
                          name={IconNames.STAR}
                          solid={true}
                          size="xs"
                          className="-mt-[0.05rem]"
                        />
                        <div>
                          {Number.isInteger(listing.reviewsAverageRating)
                            ? listing.reviewsAverageRating?.toFixed(1)
                            : listing.reviewsAverageRating?.toFixed(2)}
                        </div>

                        {listing.reviewsCount && (
                          <div className="pl-0.5">
                            {`( ` + `${listing.reviewsCount}` + ` )`}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="flex flex-row w-full justify-between space-x-1 max-w-sm">
                  <div className="flex-shrink text-gray-main text-xs font-light line-clamp-1">
                    {propertyType ? `${propertyType} in ${location}` : location}
                  </div>
                </div>
                {shouldShowGuestCapacity ? (
                  <div className="flex-shrink text-gray-main text-xs font-light line-clamp-1">
                    <div className="flex space-x-1.5">
                      {listing.numberOfRooms ? (
                        <div className="flex items-center">
                          {listing.numberOfRooms}{' '}
                          {listing.numberOfRooms > 1 ? ' bedrooms' : ' bedroom'}
                        </div>
                      ) : null}
                      {listing.numberOfRooms && listing.guestCapacity ? (
                        <div className="flex items-center">&bull;</div>
                      ) : null}
                      {listing.guestCapacity ? (
                        <>
                          <div className="flex items-center">
                            {listing.guestCapacity}{' '}
                            {listing.guestCapacity > 1 ? ' guests' : ' guest'}
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {listing.account.companyName ? (
                  <div className="flex flex-row w-full justify-between space-x-1 max-w-sm">
                    <div className="flex-shrink text-gray-main text-xs font-light line-clamp-1">
                      Hosted by {listing.account.companyName}
                    </div>{' '}
                  </div>
                ) : null}

                {displayStay && listing.priceDisplay ? (
                  <div className="flex-shrink line-clamp-1 text-gray-main text-xs font-light text-ellipsis overflow-hidden">
                    {formatDateMMMD(moment(listing.priceDisplay.checkInDate))}
                    {' – '}
                    {formatDateMMMD(moment(listing.priceDisplay.checkOutDate))}
                  </div>
                ) : null}

                <div className="hidden lg:flex text-gray-dark text-xs font-semibold my-0.5">
                  {listing.maxPrice || listing.minPrice ? (
                    <div className="flex space-x-1 text-gray-dark text-xs font-semibold text-ellipsis overflow-hidden pt-0.5">
                      {listing.priceDisplay &&
                      listing.priceDisplay.baseAmountCents > 100 ? (
                        <>
                          <div className="line-through text-gray-main">
                            {formatWholeDollarAmount(
                              toInteger(
                                rateBeforeSavings(
                                  listing.priceDisplay.baseAmountCents /
                                    listing.priceDisplay.nights
                                )
                              )
                            )}
                          </div>
                          <div className="text-primary-main">
                            {formatWholeDollarAmount(
                              toInteger(
                                listing.priceDisplay.baseAmountCents /
                                  listing.priceDisplay.nights
                              )
                            )}
                          </div>
                          <div className="text-primary-main font-medium">
                            night
                          </div>
                        </>
                      ) : listing.minPrice || listing.maxPrice ? (
                        listing.minPrice && listing.maxPrice ? (
                          <>
                            <div>Averages</div>
                            <div className="line-through text-gray-main">
                              {formatWholeDollarAmount(
                                toInteger(
                                  rateBeforeSavings(
                                    (listing.minPrice + listing.maxPrice) / 2
                                  )
                                )
                              )}
                            </div>
                            <div className="text-primary-main">
                              {formatWholeDollarAmount(
                                toInteger(
                                  (listing.minPrice + listing.maxPrice) / 2
                                )
                              )}
                            </div>
                            <div className="font-medium text-primary-main">
                              night
                            </div>
                          </>
                        ) : listing.minPrice ? (
                          <>
                            <div>Starts at</div>
                            <div className="line-through text-gray-main">
                              {formatWholeDollarAmount(
                                toInteger(rateBeforeSavings(listing.minPrice))
                              )}
                            </div>
                            <div className="text-primary-main">
                              {formatWholeDollarAmount(listing.minPrice)}
                            </div>
                            <div className="font-medium text-primary-main">
                              night
                            </div>
                          </>
                        ) : listing.maxPrice ? (
                          <>
                            <div>Peaks at</div>
                            <div className="line-through text-gray-main">
                              {formatWholeDollarAmount(
                                toInteger(rateBeforeSavings(listing.maxPrice))
                              )}
                            </div>
                            <div className="text-primary-main">
                              {formatWholeDollarAmount(listing.maxPrice)}
                            </div>
                            <div className="font-medium text-primary-main">
                              night
                            </div>
                          </>
                        ) : null
                      ) : null}
                      {listing.maxPrice && !listing.minPrice ? (
                        <>
                          <div className="text-primary-main">
                            {`Peaks at ${formatWholeDollarAmount(
                              listing.maxPrice
                            )}`}
                          </div>
                          <div className="font-medium text-primary-main">
                            night
                          </div>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </a>
              <div className="w-10 -mt-2.5 pt-2 mb-8 lg:hidden">
                <FavoriteButton
                  listingID={listing.id}
                  listingIsFavorited={isFavorited}
                  onFavoritedSuccess={onFavoritedSuccess}
                />
              </div>
            </div>

            <div className="flex pb-2 justify-between pr-3 lg:pb-3">
              <div>
                {listing.maxPrice || listing.minPrice ? (
                  <div className="flex lg:hidden space-x-1 text-gray-dark text-xs font-semibold text-ellipsis overflow-hidden pt-0.5">
                    {listing.priceDisplay &&
                    listing.priceDisplay.baseAmountCents > 100 ? (
                      <>
                        <div className="line-through text-gray-main">
                          {formatWholeDollarAmount(
                            toInteger(
                              rateBeforeSavings(
                                listing.priceDisplay.baseAmountCents /
                                  listing.priceDisplay.nights
                              )
                            )
                          )}
                        </div>
                        <div className="text-primary-main">
                          {formatWholeDollarAmount(
                            toInteger(
                              listing.priceDisplay.baseAmountCents /
                                listing.priceDisplay.nights
                            )
                          )}
                        </div>
                        <div className="font-medium text-primary-main">
                          night
                        </div>
                      </>
                    ) : listing.minPrice || listing.maxPrice ? (
                      listing.minPrice && listing.maxPrice ? (
                        <>
                          <div>Averages</div>
                          <div className="line-through text-gray-main">
                            {formatWholeDollarAmount(
                              toInteger(
                                rateBeforeSavings(
                                  (listing.minPrice + listing.maxPrice) / 2
                                )
                              )
                            )}
                          </div>
                          <div className="text-primary-main">
                            {formatWholeDollarAmount(
                              toInteger(
                                (listing.minPrice + listing.maxPrice) / 2
                              )
                            )}
                          </div>
                          <div className="font-medium text-primary-main">
                            night
                          </div>
                        </>
                      ) : listing.minPrice ? (
                        <>
                          <div>Starts at</div>
                          <div className="line-through text-gray-main">
                            {formatWholeDollarAmount(
                              toInteger(rateBeforeSavings(listing.minPrice))
                            )}
                          </div>
                          <div className="text-primary-main">
                            {formatWholeDollarAmount(listing.minPrice)}
                          </div>
                          <div className="font-medium text-primary-main">
                            night
                          </div>
                        </>
                      ) : listing.maxPrice ? (
                        <>
                          <div>Peaks at</div>
                          <div className="line-through text-gray-main">
                            {formatWholeDollarAmount(
                              toInteger(rateBeforeSavings(listing.maxPrice))
                            )}
                          </div>
                          <div className="text-primary-main">
                            {formatWholeDollarAmount(listing.maxPrice)}
                          </div>
                          <div className="font-medium text-primary-main">
                            night
                          </div>
                        </>
                      ) : null
                    ) : null}
                    {listing.maxPrice && !listing.minPrice ? (
                      <>
                        <div className="text-primary-main">
                          {`Peaks at ${formatWholeDollarAmount(
                            listing.maxPrice
                          )}`}
                        </div>
                        <div className="font-medium text-primary-main">
                          night
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="absolute top-0 right-0 pt-2 pr-2 lg:pt-3 lg:pr-3 hidden lg:block">
            <FavoriteButton
              listingID={listing.id}
              listingIsFavorited={isFavorited}
              onFavoritedSuccess={onFavoritedSuccess}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
